/*******************************************************************
* PAGE        : AddEditTicketRate 
* REMARKS     :
* CREATED ON  : 27-08-2026
* AUTHOR      : Bamroliya.Paras
* MODIFY      : Bamroliya.Paras
* MODIFY ON   : 27-08-2026
********************************************************************/

import React, { useEffect, useState } from 'react';
import DialogComponent from '../../components/DialogComponent/DialogComponent';
import InputComponent from '../../components/InputComponent/InputComponent';
import RadioboxComponent from '../../components/RadioboxComponent/RadioboxComponent';
import { CreateTicketClassificationRate, GetPromoCodes } from '../../services/apiController';
import NotificationComponent from '../../components/NotificationComponent/NotificationComponent';
import DropdownComponent from '../../components/DropdownComponent/DropdownComponent';

// #region Component
const AddEditTicketRate = ({ openPopup, eventMasterId = 0, selectedData = {}, handleClose }) => {

    // #region States
    const [loading, setLoading] = useState(false);

    const [errors, setErrors] = useState({}); // To store validation errors
    const [formData, setFormData] = useState({
        ticketPromoCodeId: selectedData?.TicketPromoCodeId || 0,
        ticketType: selectedData?.TicketType || '',
        ticketRate: selectedData?.TicketRate || '',
        minimumTickets: selectedData?.MinimumTickets || '',
        maxTicket: selectedData?.MaxTicket || '',
        rateIsActive: selectedData?.IsActive ?? true,

        applicableStartDate: selectedData?.ApplicableStartDateTime
            ? selectedData.ApplicableStartDateTime.split(' ')[0]
            : '',

        applicableStartTime: selectedData?.ApplicableStartDateTime
            ? selectedData.ApplicableStartDateTime.split(' ')[1]?.substring(0, 5)
            : '',

        applicableEndDate: selectedData?.ApplicableEndDateTime
            ? selectedData.ApplicableEndDateTime.split(' ')[0]
            : '',

        applicableEndTime: selectedData?.ApplicableEndDateTime
            ? selectedData.ApplicableEndDateTime.split(' ')[1]?.substring(0, 5)
            : '',
    });

    const [promocodeData, setPromocodeData] = useState([]);

    // State to manage snackbar visibility and messages
    const [snackbarStates, setSnackbarStates] = useState({ isSnackbarOpen: false, snackbarMessage: '' });
    const handleCloseSnackbar = () => {
        setSnackbarStates({ isSnackbarOpen: false, snackbarMessage: '', snackbarSeverity: '' });
    };
    // #endregion

    useEffect(() => {
        wsGetPromoCodes();
    }, []); // Re-run the effect when rowsPerPage or currentPage changes

    const wsGetPromoCodes = async () => {
        setLoading(true);
        GetPromoCodes({ ticketMasterId: eventMasterId })
            .then((response) => {
                if (response) {
                    const promoCodes = response.promoCodes;

                    const formattedData = (promoCodes || []).map(item => ({
                        ...item,
                        id: item.TicketPromoCodeId,
                        value: item.PromoCode,
                        // value: item.MinimumTickets > 1 ? `${item.TicketType} ( ${item.MinimumTickets} Tickets With Round Table ) ₹${inrFormatter.format(item.TicketRate)}` : `${item.TicketType} ₹${inrFormatter.format(item.TicketRate)}`,
                    }));

                    setPromocodeData(formattedData);
                } else {
                    // Handle error scenario
                }
                setLoading(false);
            }).catch((error) => {
                setLoading(false);
                // Handle API error
            });
    }


    // #region Methods
    const handleChange = (e, type, maxLength) => {
        const { name, value } = e.target;

        setFormData((prev) => ({ ...prev, [name]: value }));

        // Clear error when user starts typing
        if (errors[name]) {
            setErrors((prev) => ({ ...prev, [name]: '' }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.ticketPromoCodeId || Number(formData.ticketRate) <= 0) {
            newErrors.ticketPromoCodeId = "Please select ticket promocode";
        }

        if (!formData.ticketType.trim()) {
            newErrors.ticketType = "Please Enter Ticket Type";
        }

        if (!formData.ticketRate || Number(formData.ticketRate) <= 0) {
            newErrors.ticketRate = "Please enter ticketRate";
        }

        if (!formData.minimumTickets || Number(formData.minimumTickets) <= 0) {
            newErrors.minimumTickets = "Please enter minimum tickets limit";
        }

        if (!formData.maxTicket || Number(formData.maxTicket) <= 0) {
            newErrors.maxTicket = "Please enter maximum tickets limit";
        }

        if (!formData.applicableStartDate) {
            newErrors.applicableStartDate = "Please enter Applicable Start Date";
        }

        if (!formData.applicableStartTime) {
            newErrors.applicableStartTime = "Please enter Applicable Start Time";
        }

        if (!formData.applicableEndDate) {
            newErrors.applicableEndDate = "Please enter Applicable End Date";
        }

        if (!formData.applicableEndTime) {
            newErrors.applicableEndTime = "Please enter Applicable End Time";
        }

        setErrors(newErrors);

        // Focus first error field
        const firstErrorField = Object.keys(newErrors)[0];

        if (firstErrorField) {
            const element = document.querySelector(`[name="${firstErrorField}"]`);
            if (element) {
                element.focus();
                element.scrollIntoView({ behavior: "smooth", block: "center" });
            }
        }
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {

        if (!validateForm()) {
            return;
        }

        setLoading(true);

        const applicableStartDateTime = `${formData.applicableStartDate} ${formData.applicableStartTime}:00`;

        const applicableEndDateTime = `${formData.applicableEndDate} ${formData.applicableEndTime}:00`;

        const promoParameter = {
            ticketMasterId: eventMasterId,
            ticketPromoCodeId: formData?.ticketPromoCodeId,
            ticketType: formData.ticketType,
            ticketRate: formData.ticketRate,
            minimumTickets: formData.minimumTickets,
            maxTicket: formData.maxTicket,
            isActive: formData.rateIsActive,

            applicableStartDateTime,
            applicableEndDateTime,
        };

        try {
            const response = await CreateTicketClassificationRate(promoParameter);
            if (response?.status) {
                setSnackbarStates({ isSnackbarOpen: true, snackbarMessage: response?.message || "Ticket Rate create successfully", snackbarSeverity: "success" });
            } else {
                setSnackbarStates({ isSnackbarOpen: true, snackbarMessage: response?.message || "Unable to save event", snackbarSeverity: "error" });
            }
            handleClose();
        } catch (error) {
            setSnackbarStates({ isSnackbarOpen: true, snackbarMessage: "Something went wrong", snackbarSeverity: "error" });
        } finally {
            setLoading(false);
        }
    }

    // #endregion

    return (
        <React.Fragment>
            <NotificationComponent isOpen={snackbarStates.isSnackbarOpen} message={snackbarStates.snackbarMessage} onClose={handleCloseSnackbar} severity={snackbarStates.snackbarSeverity} />
            <DialogComponent
                isOpen={openPopup}
                Title="Ticket Rate"
                maxWidth={'md'}
                ActionText={"Save"}
                IsLoading={loading}
                onSave={handleSubmit}
                onClose={handleClose}
            >
                <form onSubmit={handleSubmit}>
                    <div className="form-grid" style={{ marginBottom: '20px', }}>
                        <div style={{ marginTop: '20px' }}>
                            <label>Select Promocode*</label>
                            <DropdownComponent name="ticketPromoCodeId" placeholder="Select Promocode*" value={formData.ticketPromoCodeId} onChange={handleChange} error={errors.ticketPromoCodeId} options={promocodeData} />
                        </div>

                        <div style={{ marginTop: '20px' }}>
                            <label>Ticket Type*</label>
                            <InputComponent name="ticketType" placeholder="Ticket Type*" value={formData.ticketType} onChange={handleChange} error={errors.ticketType} maxLength={25} />
                        </div>

                        <div>
                            <label>Ticket Rate*</label>
                            <InputComponent type="number" name="ticketRate" placeholder="Ticket Rate*" value={formData.ticketRate} onChange={handleChange} error={errors.ticketRate} maxLength={7} />
                        </div>

                        <div>
                            <label>Minimum Tickets*</label>
                            <InputComponent type='number' name="minimumTickets" placeholder="Minimum Tickets*" value={formData.minimumTickets} onChange={handleChange} error={errors.minimumTickets} maxLength={5} />
                        </div>

                        <div>
                            <label>Maximum Ticket*</label>
                            <InputComponent type='number' name="maxTicket" placeholder="Maximum Ticket*" value={formData.maxTicket} onChange={handleChange} error={errors.maxTicket} maxLength={5} />
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <div style={{ width: '70%', marginRight: '8px' }}>
                                <label>Applicable Start Date*</label>
                                <InputComponent type='date' name="applicableStartDate" placeholder="Applicable Start Date*" value={formData.applicableStartDate} onChange={handleChange} error={errors.applicableStartDate} />
                            </div>
                            <div style={{ width: '30%', marginLeft: '8px' }}>
                                <label>Start Time*</label>
                                <InputComponent type='time' name="applicableStartTime" placeholder="Start Time*" value={formData.applicableStartTime} onChange={handleChange} error={errors.applicableStartTime} />
                            </div>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <div style={{ width: '70%', marginRight: '8px' }}>
                                <label>Applicable End Date*</label>
                                <InputComponent type='date' name="applicableEndDate" placeholder="Applicable End Date*" value={formData.applicableEndDate} onChange={handleChange} error={errors.applicableEndDate} />
                            </div>
                            <div style={{ width: '30%', marginLeft: '8px' }}>
                                <label>End Time*</label>
                                <InputComponent type='time' name="applicableEndTime" placeholder="End Time*" value={formData.applicableEndTime} onChange={handleChange} error={errors.applicableEndTime} />
                            </div>
                        </div>

                        <div style={{ marginTop: '20px', display: 'flex' }}>
                            <label style={{ width: '100%' }}>Is Active ?</label>

                            <RadioboxComponent
                                uniqueId="promo-yes"
                                name="rateIsActive"
                                value="true"
                                checked={formData.rateIsActive === true}
                                onChange={(e) => setFormData(prev => ({ ...prev, rateIsActive: e.target.value === 'true' }))}
                                label={<label htmlFor="promo-yes" className="event-radio-card-label">
                                    <span className="event-radio-card-text">Yes</span>
                                </label>}
                            />

                            <RadioboxComponent
                                uniqueId="promo-no"
                                name="rateIsActive"
                                value="false"
                                checked={formData.rateIsActive === false}
                                onChange={(e) => setFormData(prev => ({ ...prev, rateIsActive: e.target.value === 'true' }))}
                                label={<label htmlFor="promo-no" className="event-radio-card-label">
                                    <span className="event-radio-card-text">No</span>
                                </label>}
                            />
                        </div>

                    </div>

                    <button type="submit" style={{ display: "none" }} />
                </form>

            </DialogComponent>
        </React.Fragment>
    );
};
// #endregion

export default AddEditTicketRate;